import React, { useMemo, useState } from "react";
import { FormField } from "../Common/FormComponents";

// ── Styles ─────────────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1d23",
    flexShrink: 0,
  },
  body: { padding: "1.25rem", overflowY: "auto", flex: 1 },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6,
    border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: "pointer",
  },
  btnDanger: {
    fontSize: 12, fontWeight: 600, padding: "6px 18px", borderRadius: 6,
    border: "none", background: "#991B1B", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 20, lineHeight: 1, color: "#6b7280", padding: "0 2px",
  },
  errorBanner: {
    fontSize: 12, color: "#991B1B", background: "#FEF2F2",
    border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", marginBottom: "1rem",
  },
  validationMsg: { fontSize: 11, color: "#991B1B", marginTop: 3 },
  spinner: {
    display: "inline-block", width: 12, height: 12,
    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
    verticalAlign: "middle", marginRight: 6,
  },
};

const getTodayInputValue = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
};

const PM_AFTER_UNLINK_DEFAULT = "vacant_active";

function UnlinkTenantModal({ show, onHide, property, onConfirm, loading = false, error = "" }) {
  const [terminationDate, setTerminationDate] = useState(getTodayInputValue());
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [afterUnlink, setAfterUnlink] = useState(PM_AFTER_UNLINK_DEFAULT);
  const [showValidation, setShowValidation] = useState(false);

  const isPropertyManagement = property?.type === "property_management";

  const tenantDisplayName = useMemo(() => {
    const t =
      property?.placementInfo?.tenantInfo?.tenantId ||
      property?.managementInfo?.currentTenant ||
      property?.occupancy?.currentTenant;
    if (t?.personalInfo?.firstName && t?.personalInfo?.lastName)
      return `${t.personalInfo.firstName} ${t.personalInfo.lastName}`;
    return property?.placementInfo?.tenantInfo?.tenantName || "Current tenant";
  }, [property]);

  const handleClose = () => {
    if (loading) return;
    setTerminationDate(getTodayInputValue());
    setReason("");
    setNotes("");
    setAfterUnlink(PM_AFTER_UNLINK_DEFAULT);
    setShowValidation(false);
    onHide?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedReason = reason.trim();
    if (!terminationDate || !trimmedReason) { setShowValidation(true); return; }
    onConfirm?.({
      terminationDate,
      reason: trimmedReason,
      notes: notes.trim(),
      ...(isPropertyManagement ? { afterUnlink } : {}),
    });
  };

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={handleClose}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal} onClick={(e) => e.stopPropagation()}>
        <div style={ms.head}>
          Unlink Tenant
          {!loading && <button style={ms.closeBtn} onClick={handleClose}>×</button>}
        </div>

        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
          <div style={ms.body}>
            <p style={{ fontSize: 13, marginBottom: "1rem" }}>
              You are unlinking <strong>{tenantDisplayName}</strong> from this property.
            </p>

            {error && <div style={ms.errorBanner}>{error}</div>}

            <div style={{ marginBottom: "0.875rem" }}>
              <FormField
                label="Termination Date"
                type="date"
                name="terminationDate"
                value={terminationDate}
                onChange={(e) => setTerminationDate(e.target.value)}
                required
                disabled={loading}
              />
              {showValidation && !terminationDate && (
                <div style={ms.validationMsg}>Termination date is required.</div>
              )}
            </div>

            {isPropertyManagement && (
              <div style={{ marginBottom: "0.875rem" }}>
                <FormField
                  label="After this tenant"
                  name="afterUnlink"
                  type="select"
                  value={afterUnlink}
                  onChange={(e) => setAfterUnlink(e.target.value)}
                  disabled={loading}
                  helpText="Choose whether the unit stays in your management portfolio as vacant, or you stop managing this property."
                  options={[
                    { value: "vacant_active", label: "Vacant — still managing (active listing, ready for next tenant)" },
                    { value: "end_management", label: "End management (mark inactive / off active portfolio)" },
                  ]}
                />
              </div>
            )}

            <div style={{ marginBottom: "0.875rem" }}>
              <FormField
                label="Reason"
                name="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                placeholder="e.g. Lease ended by mutual agreement"
                disabled={loading}
              />
              {showValidation && !reason.trim() && (
                <div style={ms.validationMsg}>Reason is required.</div>
              )}
            </div>

            <FormField
              label="Notes (optional)"
              name="notes"
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any extra context..."
              disabled={loading}
            />
          </div>

          <div style={ms.foot}>
            <button type="button" style={ms.btn} onClick={handleClose} disabled={loading}>Cancel</button>
            <button type="submit" style={ms.btnDanger} disabled={loading}>
              {loading ? <><span style={ms.spinner} />Unlinking…</> : "Unlink Tenant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UnlinkTenantModal;
